import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { parseApiError } from '@/lib/api-error';

const API_URL = import.meta.env.VITE_API_URL ?? '';

type View = 'redirecting' | 'error';

/**
 * /connect/:token — public (account-less) onboarding link entry point.
 *
 * An org admin distributes this URL to their end-customers. On mount the page
 * calls `GET /org/connect/:token/start`, which mints an onboarding OAuth state
 * row and returns the provider authorization URL: Meta Embedded Signup for
 * WhatsApp or direct Instagram OAuth for Instagram. The browser is then
 * immediately navigated there (full-page, not a client-side router Navigate).
 *
 * On error (revoked link, inactive org) the page shows a neutral failure
 * message — no internal detail is leaked.
 *
 * No WorkOS session, no auth headers — entirely public.
 */
export function ConnectStartPage() {
  const { token } = useParams<{ token: string }>();

  // The router guarantees :token is present when this component renders
  // (the route is /connect/:token). Defensively initialize the error state
  // here rather than in a useEffect so we don't trip react-hooks/set-state-in-effect.
  const [view, setView] = useState<View>(() => (token ? 'redirecting' : 'error'));
  const [errorMsg, setErrorMsg] = useState<string | null>(() =>
    token ? null : 'This link is no longer active.',
  );
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    // token is always defined here (see lazy initializer comment above), but
    // TypeScript types it as string | undefined so we short-circuit explicitly.
    if (!token) return;

    void (async () => {
      try {
        const res = await fetch(`${API_URL}/org/connect/${encodeURIComponent(token)}/start`);

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const apiError = parseApiError(
            res.status,
            data,
            'This link is no longer active.',
          );
          setErrorMsg(apiError.userMessage);
          setView('error');
          return;
        }

        const data = (await res.json()) as { redirectUrl?: string };
        if (!data.redirectUrl) {
          setErrorMsg('This link is no longer active.');
          setView('error');
          return;
        }

        window.location.href = data.redirectUrl;
      } catch {
        setErrorMsg('Connection error. Please try the link again.');
        setView('error');
      }
    })();
  }, [token]);

  const center: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: 'system-ui',
    padding: 24,
    textAlign: 'center',
  };

  if (view === 'error') {
    return (
      <div style={center}>
        <p>{errorMsg}</p>
      </div>
    );
  }

  // redirecting — start request in-flight or window.location.href firing
  return (
    <div style={{ ...center, background: '#1C1E21', color: '#fff' }}>
      <p>Redirecting...</p>
    </div>
  );
}
